import uniq from "lodash-es/uniq";
import { stringify } from "query-string";
import { Reducer, combineReducers } from "redux";

import { serializeToQS } from "@/mk2/apps/strollers/containers/StrollersSearch/StrollerSearchForm";
import {
    STROLLERS_SEARCH_LOAD_FACETS_API_SUCCESS,
    STROLLERS_SEARCH_LOAD_HELP_API_SUCCESS,
    STROLLERS_SEARCH_ON_OPEN_SELECT_FACET_VALUE,
    STROLLERS_SEARCH_RESULTS_FETCH_FAILURE,
    STROLLERS_SEARCH_RESULTS_FETCH_REQUEST,
    STROLLERS_SEARCH_RESULTS_FETCH_SUCCESS,
    STROLLERS_SEARCH_TOGGLE_MORE_FACETS,
    SearchFacetsCounts,
    StrollersSearchLoadFacetsApiSuccessAction,
    StrollersSearchLoadHelpApiSuccessAction,
    StrollersSearchOnOpenSelectFacetValueAction,
    StrollersSearchResultsFetchFailureAction,
    StrollersSearchResultsFetchRequestAction,
    StrollersSearchResultsFetchSuccessAction,
    StrollersSearchToggleMoreFacetsAction,
} from "@/mk2/apps/strollers/containers/StrollersSearch/StrollersSearch.actions";
import { FacetSetup } from "@/mk2/apps/strollers/containers/StrollersSearch/StrollersSearch.constants";
import { StrollerPhoto } from "@/mk2/apps/strollers/schemas";
import { tupdate } from "@/mk2/helpers/types";
import { errorMessageFiltered } from "@/mk2/reducers/errorMessage";
import { LoadingState, loadingStateFiltered } from "@/mk2/reducers/loadingState";

export interface SearchFacetsCountsAndHits {
    counts: SearchFacetsCounts;
    hits: number;
}

export interface StrollersSearchState {
    showAdditionalFacets: boolean;
    actuallyDisplayedFacet: FacetSetup;

    buyingGuideId: number;
    brandIds: number[];
    brandsCount: number;
    strollersCountPerBrand: Record<number, number>;
    reviewsCountPerBrand: Record<number, number>;

    searchFacetsCounts: Record<string, SearchFacetsCountsAndHits>;
    helpTexts: number[];

    results: StrollersResultsState;
}

export const initialStrollersSearchState: StrollersSearchState = {
    showAdditionalFacets: false,
    actuallyDisplayedFacet: null,

    buyingGuideId: null,
    brandIds: [],
    brandsCount: 0,
    strollersCountPerBrand: {},
    reviewsCountPerBrand: {},

    searchFacetsCounts: {},
    helpTexts: [],

    results: {},
};

export interface StrollersResultState {
    strollerIds: number[];
    strollersPreviewPhotoMap: Record<number, number>;
    variantIds: number[];
    variantsPreviewPhotoMap: Record<number, number>;
    strollersPhotosIds: number[];
    brandsOfQueryIds: number[];
    searchInfoIds: number[];
    loadingState: LoadingState;
    errorMessage: string;
    hasMore: boolean;
    count: number;
}

export const initialStrollersResultState: StrollersResultState = {
    strollerIds: [],
    strollersPreviewPhotoMap: {},
    variantIds: [],
    variantsPreviewPhotoMap: {},
    strollersPhotosIds: [],
    brandsOfQueryIds: [],
    searchInfoIds: [],
    loadingState: LoadingState.INIT,
    errorMessage: null,
    hasMore: false,
    count: null,
};

export interface StrollersResultsState {
    [q: string]: StrollersResultState;
}

const hasMore: Reducer<boolean> = (
    state: boolean = true,
    action: StrollersSearchResultsFetchSuccessAction,
) => {
    switch (action.type) {
        case STROLLERS_SEARCH_RESULTS_FETCH_SUCCESS:
            return action.hasMore;
        default:
            return state;
    }
};

const count: Reducer<number> = (
    state: number = null,
    action: StrollersSearchResultsFetchSuccessAction,
) => {
    const firstFetch = !action.offset;
    switch (action.type) {
        case STROLLERS_SEARCH_RESULTS_FETCH_SUCCESS:
            return firstFetch ? action.count : state;
        default:
            return state;
    }
};

const strollersResultReducer: Reducer<StrollersResultState> = combineReducers<StrollersResultState>(
    {
        loadingState: loadingStateFiltered("STROLLERS_SEARCH_RESULTS_FETCH"),
        errorMessage: errorMessageFiltered("STROLLERS_SEARCH_RESULTS_FETCH"),
        searchInfoIds: (state = [], action) => {
            switch (action.type) {
                case STROLLERS_SEARCH_RESULTS_FETCH_SUCCESS:
                    return action.response.result.searchInfos;
                default:
                    return state;
            }
        },
        strollerIds: (state = [], action) => {
            switch (action.type) {
                case STROLLERS_SEARCH_RESULTS_FETCH_SUCCESS:
                    return uniq([...state, ...action.response.result.strollers]);
                default:
                    return state;
            }
        },
        strollersPreviewPhotoMap: (state = {}, action) => {
            switch (action.type) {
                case STROLLERS_SEARCH_RESULTS_FETCH_SUCCESS:
                    return tupdate(state, action.response.result.strollersPreviewPhotoMap);
                default:
                    return state;
            }
        },
        variantIds: (state = [], action) => {
            switch (action.type) {
                case STROLLERS_SEARCH_RESULTS_FETCH_SUCCESS:
                    return uniq([...state, ...action.response.result.variants]);
                default:
                    return state;
            }
        },
        variantsPreviewPhotoMap: (state = {}, action) => {
            switch (action.type) {
                case STROLLERS_SEARCH_RESULTS_FETCH_SUCCESS:
                    return tupdate(state, action.response.result.variantsPreviewPhotoMap);
                default:
                    return state;
            }
        },
        strollersPhotosIds: (state = [], action) => {
            switch (action.type) {
                case STROLLERS_SEARCH_RESULTS_FETCH_SUCCESS:
                    return uniq([...state, ...action.response.result.strollersPhotos]);
                default:
                    return state;
            }
        },
        brandsOfQueryIds: (state = [], action) => {
            switch (action.type) {
                case STROLLERS_SEARCH_RESULTS_FETCH_SUCCESS:
                    return uniq([...state, ...action.response.result.brandsOfQuery]);
                default:
                    return state;
            }
        },
        hasMore,
        count,
    },
);

declare type StrollersSearchReducerAction =
    | StrollersSearchToggleMoreFacetsAction
    | StrollersSearchOnOpenSelectFacetValueAction
    | StrollersSearchResultsFetchRequestAction
    | StrollersSearchResultsFetchSuccessAction
    | StrollersSearchResultsFetchFailureAction
    | StrollersSearchLoadFacetsApiSuccessAction
    | StrollersSearchLoadHelpApiSuccessAction;

export const strollersSearchReducer: Reducer<StrollersSearchState> = (
    state = initialStrollersSearchState,
    action: StrollersSearchReducerAction,
) => {
    const strollersSearchState = state || initialStrollersSearchState;
    switch (action.type) {
        case STROLLERS_SEARCH_TOGGLE_MORE_FACETS:
            return tupdate(strollersSearchState, {
                showAdditionalFacets: !strollersSearchState.showAdditionalFacets,
            });
        case STROLLERS_SEARCH_ON_OPEN_SELECT_FACET_VALUE:
            return tupdate(strollersSearchState, {
                actuallyDisplayedFacet: action.facetSetup,
            });
        case STROLLERS_SEARCH_RESULTS_FETCH_REQUEST:
        case STROLLERS_SEARCH_RESULTS_FETCH_SUCCESS:
        case STROLLERS_SEARCH_RESULTS_FETCH_FAILURE: {
            const firstFetch = !action.offset;
            const q = action.q;
            let strollersSearchResultsState: StrollersResultsState = state.results || {};
            strollersSearchResultsState = {
                ...state.results,
                [q]: strollersResultReducer(strollersSearchResultsState[q], action),
            };
            let changedState = tupdate(strollersSearchState, {
                results: strollersSearchResultsState,
            });

            if (firstFetch) {
                switch (action.type) {
                    case STROLLERS_SEARCH_RESULTS_FETCH_SUCCESS:
                        changedState = tupdate(changedState, {
                            brandIds: action.response.result.brands,
                            brandsCount: action.brandsCount,
                            strollersCountPerBrand: action.strollersCountPerBrand,
                            reviewsCountPerBrand: action.reviewsCountPerBrand,
                            buyingGuideId: action.response.result.buyingGuide,
                        });
                }
            }
            return changedState;
        }
        case STROLLERS_SEARCH_LOAD_FACETS_API_SUCCESS: {
            const key = stringify(serializeToQS(action.values));
            return {
                ...state,
                searchFacetsCounts: {
                    ...state.searchFacetsCounts,
                    [key]: { counts: action.searchFacetsCounts, hits: action.hits },
                },
            };
        }
        case STROLLERS_SEARCH_LOAD_HELP_API_SUCCESS:
            return {
                ...state,
                helpTexts: action.response.result.items,
            };
        default:
            return state;
    }
};

export default strollersSearchReducer;
